import { useMemo, useState } from 'react'
import {
  BadgeStatus,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  Input,
  KolomForm,
  Modal,
  Select,
  Textarea,
  Checkbox,
  useToast,
  type KolomTabel,
  type OpsiSelect,
} from '@/components'
import { api, pesanKesalahan } from '@/lib/api'
import { formatAngka, formatRupiah, formatTanggal } from '@/lib/format'
import { usePaged } from '@/lib/useApi'

export interface FieldCrud {
  kunci: string
  label: string
  tipe?: 'teks' | 'angka' | 'tanggal' | 'pilih' | 'area' | 'centang'
  opsi?: OpsiSelect[]
  wajib?: boolean
  bantuan?: string
  nilaiBawaan?: string | boolean
  lebarPenuh?: boolean
}

export interface KolomCrud {
  kunci: string
  judul: string
  tipe?: 'teks' | 'angka' | 'rupiah' | 'tanggal' | 'status'
  lebar?: string
  rata?: 'kiri' | 'kanan' | 'tengah'
}

/**
 * Panel CRUD generik untuk Master Data.
 * Memakai endpoint daftar (GET) + POST untuk menambah + PATCH `${path}/:id` untuk mengubah.
 */
export function PanelCrud({
  path,
  judul,
  deskripsi,
  field,
  kolom,
  bisaTambah = true,
  bisaUbah = true,
  pathUbah,
  queryTambahan,
  nilaiBawaan,
  kunciBaris = 'id',
}: {
  path: string
  judul: string
  deskripsi?: string
  field: FieldCrud[]
  kolom: KolomCrud[]
  bisaTambah?: boolean
  bisaUbah?: boolean
  pathUbah?: (id: string) => string
  queryTambahan?: Record<string, string | undefined>
  nilaiBawaan?: Record<string, string | boolean>
  kunciBaris?: string
}) {
  const toast = useToast()
  const [cari, setCari] = useState('')
  const daftar = usePaged<Record<string, unknown>>(path, {
    perHalaman: 100,
    query: { q: cari.trim() || undefined, ...queryTambahan },
  })

  const [terbuka, setTerbuka] = useState(false)
  const [nilai, setNilai] = useState<Record<string, string | boolean>>({})
  const [idUbah, setIdUbah] = useState<string | null>(null)
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)
  const [hapusId, setHapusId] = useState<string | null>(null)

  const kolomTabel = useMemo<KolomTabel<Record<string, unknown>>[]>(() => {
    const hasil: KolomTabel<Record<string, unknown>>[] = kolom.map((k) => ({
      kunci: k.kunci,
      judul: k.judul,
      lebar: k.lebar,
      rata: k.rata,
      render: (b) => renderNilai(b[k.kunci], k.tipe),
    }))
    if (bisaUbah) {
      hasil.push({
        kunci: '__aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <Button
            ukuran="kecil"
            varian="garis"
            onClick={() => {
              setIdUbah(String(b[kunciBaris] ?? b.id))
              const awal: Record<string, string | boolean> = {}
              for (const f of field) {
                const v = b[f.kunci]
                awal[f.kunci] =
                  typeof v === 'boolean' ? v : v === null || v === undefined ? '' : String(v)
              }
              setNilai(awal)
              setGalat(null)
              setTerbuka(true)
            }}
          >
            Ubah
          </Button>
        ),
      })
    }
    return hasil
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kolom, field, bisaUbah, kunciBaris])

  const bukaTambah = () => {
    const awal: Record<string, string | boolean> = { ...(nilaiBawaan ?? {}) }
    for (const f of field) {
      if (awal[f.kunci] === undefined) {
        awal[f.kunci] = f.tipe === 'centang' ? Boolean(f.nilaiBawaan) : f.nilaiBawaan !== undefined ? String(f.nilaiBawaan) : ''
      }
    }
    setNilai(awal)
    setIdUbah(null)
    setGalat(null)
    setTerbuka(true)
  }

  const simpan = async () => {
    setGalat(null)
    const kurang = field.filter((f) => f.wajib && !String(nilai[f.kunci] ?? '').trim())
    if (kurang.length > 0) {
      setGalat(`Kolom wajib belum diisi: ${kurang.map((f) => f.label).join(', ')}.`)
      return
    }

    const muatan: Record<string, unknown> = {}
    for (const f of field) {
      const v = nilai[f.kunci]
      if (f.tipe === 'centang') {
        muatan[f.kunci] = Boolean(v)
      } else if (f.tipe === 'angka') {
        const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''))
        muatan[f.kunci] = Number.isFinite(n) ? n : undefined
      } else {
        const teks = String(v ?? '').trim()
        muatan[f.kunci] = teks === '' ? undefined : teks
      }
    }

    setMemuat(true)
    try {
      if (idUbah) {
        const rute = pathUbah ? pathUbah(idUbah) : `${path}/${idUbah}`
        await api.patch(rute, muatan)
        toast.sukses('Data berhasil diperbarui.')
      } else {
        await api.post(path, muatan)
        toast.sukses('Data berhasil ditambahkan.')
      }
      setTerbuka(false)
      daftar.muat()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Card padat className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-netral-200 px-4 py-3">
        <CardHeader judul={judul} deskripsi={deskripsi} />
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Cari…"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
            className="min-w-44"
          />
          <Button varian="garis" ukuran="kecil" onClick={daftar.muat}>
            Muat ulang
          </Button>
          {bisaTambah ? (
            <Button ukuran="kecil" onClick={bukaTambah}>
              + Tambah
            </Button>
          ) : null}
        </div>
      </div>

      <DataTable
        kolom={kolomTabel}
        data={daftar.items}
        kunciBaris={(b, i) => String(b[kunciBaris] ?? b.id ?? i)}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong={`Belum ada data ${judul.toLowerCase()}.`}
        aksiKosong={bisaTambah ? <Button onClick={bukaTambah}>+ Tambah {judul}</Button> : undefined}
      />

      <Modal
        terbuka={terbuka}
        onTutup={() => setTerbuka(false)}
        judul={idUbah ? `Ubah ${judul}` : `Tambah ${judul}`}
        lebar="max-w-3xl"
        footer={
          <>
            <Button varian="garis" onClick={() => setTerbuka(false)} disabled={memuat}>
              Batal
            </Button>
            <Button onClick={() => void simpan()} memuat={memuat}>
              Simpan
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {galat ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {galat}
            </div>
          ) : null}
          <KolomForm kolom={2}>
            {field.map((f) => (
              <div key={f.kunci} className={f.lebarPenuh ? 'sm:col-span-2' : ''}>
                {f.tipe === 'pilih' ? (
                  <Select
                    label={f.label}
                    opsi={f.opsi ?? []}
                    placeholder="Pilih…"
                    value={String(nilai[f.kunci] ?? '')}
                    onChange={(e) => setNilai((n) => ({ ...n, [f.kunci]: e.target.value }))}
                    required={f.wajib}
                    bantuan={f.bantuan}
                  />
                ) : f.tipe === 'area' ? (
                  <Textarea
                    label={f.label}
                    value={String(nilai[f.kunci] ?? '')}
                    onChange={(e) => setNilai((n) => ({ ...n, [f.kunci]: e.target.value }))}
                    bantuan={f.bantuan}
                  />
                ) : f.tipe === 'centang' ? (
                  <div className="pt-6">
                    <Checkbox
                      label={f.label}
                      checked={Boolean(nilai[f.kunci])}
                      onChange={(e) => setNilai((n) => ({ ...n, [f.kunci]: e.target.checked }))}
                    />
                    {f.bantuan ? <p className="mt-1 text-xs text-netral-500">{f.bantuan}</p> : null}
                  </div>
                ) : (
                  <Input
                    label={f.label}
                    type={f.tipe === 'angka' ? 'number' : f.tipe === 'tanggal' ? 'date' : 'text'}
                    value={String(nilai[f.kunci] ?? '')}
                    onChange={(e) => setNilai((n) => ({ ...n, [f.kunci]: e.target.value }))}
                    required={f.wajib}
                    bantuan={f.bantuan}
                  />
                )}
              </div>
            ))}
          </KolomForm>
        </div>
      </Modal>

      <ConfirmDialog
        terbuka={hapusId !== null}
        onTutup={() => setHapusId(null)}
        judul="Hapus Data"
        pesan="Penghapusan data master tidak disediakan oleh API — gunakan status Tidak Aktif."
        labelKonfirmasi="Mengerti"
        berbahaya={false}
        onKonfirmasi={() => setHapusId(null)}
      />
    </Card>
  )
}

function renderNilai(nilai: unknown, tipe?: KolomCrud['tipe']) {
  if (nilai === null || nilai === undefined || nilai === '') return <span className="text-netral-400">—</span>
  switch (tipe) {
    case 'rupiah':
      return formatRupiah(Number(nilai))
    case 'angka':
      return formatAngka(Number(nilai))
    case 'tanggal':
      return formatTanggal(String(nilai))
    case 'status':
      return <BadgeStatus status={String(nilai)} />
    default:
      if (typeof nilai === 'boolean') return nilai ? 'Ya' : 'Tidak'
      if (typeof nilai === 'object') {
        const o = nilai as Record<string, unknown>
        return String(o.name ?? o.fullName ?? o.code ?? JSON.stringify(nilai))
      }
      return String(nilai)
  }
}
